/**
 * Timeline geometry for the backup panel.
 *
 * Deliberately free of React, like `map/layout.ts`: it takes a `BackupHistory`
 * and a time window and returns fractions of that window, so the component
 * only has to decide how many pixels an hour is worth. That also means the
 * awkward parts — stacking overlapping runs, clipping a backup that began
 * before the window, a run still in flight — are tested without a browser.
 */

import type { BackupGuestOutcome, BackupGuestReport, BackupHistory, BackupRun } from '@proxmon/shared';

/**
 * Two runs on the same lane row need at least this much air between them.
 *
 * Without it a pair of short backups minutes apart renders as one long bar,
 * which reads as a single slow backup rather than two quick ones.
 */
const ROW_GAP_MS = 5 * 60_000;

export interface PlacedRun {
  run: BackupRun;
  /** which stacked row inside the lane, 0 = top */
  row: number;
  /** left edge as a fraction 0..1 of the window */
  left: number;
  /** width as a fraction of the window; can be 0 for a very short backup */
  width: number;
  /** the run began before the window opened, so the bar is cut off on the left */
  clippedStart: boolean;
}

export interface BackupLane {
  node: string;
  runs: PlacedRun[];
  /** how many stacked rows of bars this lane needs; always at least 1 */
  rows: number;
  /** this node's task log could not be read, so an empty lane means nothing */
  unreadable: boolean;
  /**
   * The distinct storages this host's runs wrote to, named alphabetically.
   *
   * Empty when nothing in the window says — either no runs, or their task logs
   * have not been read yet. The lane label can only be honest about one name,
   * so the common case (a host with one backup target) is the one it states;
   * the exact storage per run stays on the bar's tooltip.
   */
  storages: string[];
}

/**
 * Place every run on its host's lane.
 *
 * One lane per host, in the order given, so the panel's rows line up with the
 * hosts on the map even for a host that has never been backed up.
 */
export function buildLanes(
  nodes: readonly string[],
  history: BackupHistory,
  windowStart: number,
  windowEnd: number,
): BackupLane[] {
  const span = Math.max(1, windowEnd - windowStart);
  const unreadable = new Set(history.unreadable);
  const byNode = new Map<string, BackupRun[]>(nodes.map((node) => [node, []]));

  for (const run of history.runs) {
    // A run on a host that is no longer in the cluster has nowhere to go. It
    // is dropped rather than given a lane of its own: the panel's rows are the
    // hosts, and inventing one would misrepresent the cluster.
    byNode.get(run.node)?.push(run);
  }

  return nodes.map((node) => {
    const runs = (byNode.get(node) ?? []).slice().sort((a, b) => a.startedAt - b.startedAt);
    /** the right edge, in ms, of the last bar placed on each row */
    const rowEnds: number[] = [];
    const placed: PlacedRun[] = [];

    for (const run of runs) {
      // A backup still in flight is drawn all the way to the live edge, which
      // is what makes "this has been running for nine hours" visible at all.
      const startedAt = run.startedAt;
      const endedAt = Math.min(run.endedAt ?? windowEnd, windowEnd);

      let row = rowEnds.findIndex((end) => end + ROW_GAP_MS <= startedAt);
      if (row === -1) {
        row = rowEnds.length;
        rowEnds.push(endedAt);
      } else {
        rowEnds[row] = endedAt;
      }

      const left = Math.max(0, (startedAt - windowStart) / span);
      placed.push({
        run,
        row,
        left,
        width: Math.max(0, (endedAt - windowStart) / span - left),
        clippedStart: startedAt < windowStart,
      });
    }

    const storages = [...new Set(runs.map((run) => run.storage).filter((name): name is string => name !== null))];
    storages.sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));

    return {
      node,
      runs: placed,
      rows: Math.max(1, rowEnds.length),
      unreadable: unreadable.has(node),
      storages,
    };
  });
}

export interface BackupSummary {
  ok: number;
  warnings: number;
  failed: number;
  running: number;
  /** epoch ms the most recent clean run finished, or null if none did */
  lastSuccessAt: number | null;
}

/**
 * What the header says, folded or not.
 *
 * The point of showing this on the folded header is that collapsing the panel
 * must not hide a failed backup — the one thing somebody opening Proxmon in a
 * hurry needs to know.
 */
export function summarise(history: BackupHistory): BackupSummary {
  const summary: BackupSummary = { ok: 0, warnings: 0, failed: 0, running: 0, lastSuccessAt: null };

  for (const run of history.runs) {
    summary[run.outcome] += 1;
    if (run.outcome === 'ok' && run.endedAt !== null && run.endedAt > (summary.lastSuccessAt ?? 0)) {
      summary.lastSuccessAt = run.endedAt;
    }
  }

  return summary;
}

/**
 * How wide an hour is, in pixels, at each zoom stop.
 *
 * Fixed stops rather than a continuous scale: a bar's width has to mean the
 * same thing on a laptop and on the wall display, and doubling each time keeps
 * every stop a round multiple of the default. 46 is that default — wide enough
 * that a 40-minute backup is a bar rather than a hairline, narrow enough that
 * two days fit on a laptop.
 */
export const ZOOM_STOPS = [12, 23, 46, 92, 184, 368, 736] as const;
export const DEFAULT_ZOOM = 46;
/** The ends of the ladder, so the buttons know when to give up. */
export const ZOOM_MIN = ZOOM_STOPS[0];
export const ZOOM_MAX = ZOOM_STOPS[ZOOM_STOPS.length - 1] as number;

/** The next stop in or out, or the current one at either end of the ladder. */
export function zoomBy(pxPerHour: number, direction: 1 | -1): number {
  const stops = ZOOM_STOPS as readonly number[];
  // Nearest stop first, so a value from an older session's state still moves.
  const nearest = stops.reduce((best, stop) =>
    Math.abs(stop - pxPerHour) < Math.abs(best - pxPerHour) ? stop : best,
  );
  const index = stops.indexOf(nearest) + direction;
  return stops[Math.min(stops.length - 1, Math.max(0, index))] as number;
}

const MINUTE = 60_000;
/**
 * The intervals the axis is allowed to mark, smallest first. Every one divides
 * 24 hours, which is what lets the ticks be generated from local midnight and
 * still land on round wall-clock times.
 */
const STEPS_MS = [
  MINUTE,
  2 * MINUTE,
  5 * MINUTE,
  10 * MINUTE,
  15 * MINUTE,
  30 * MINUTE,
  60 * MINUTE,
  120 * MINUTE,
  180 * MINUTE,
  360 * MINUTE,
  720 * MINUTE,
  1440 * MINUTE,
];
/** Closest two gridlines may be drawn. Below this the lane reads as hatching. */
const MIN_TICK_PX = 30;
/**
 * Closest two labels may be drawn. Generous, because the label sits to the
 * right of its own tick: at the default zoom this is what keeps the axis on
 * six-hour marks rather than crowding it with every other hour.
 */
const MIN_LABEL_PX = 150;

export interface TickSteps {
  /** interval between gridlines, in ms */
  stepMs: number;
  /** interval between labelled gridlines, in ms; always a multiple of stepMs */
  labelStepMs: number;
}

/**
 * How finely to mark the axis at this zoom.
 *
 * Both intervals are chosen by how many pixels they would be worth, so zooming
 * in walks the axis down the ladder on its own: hours become half-hours become
 * quarter-hours, and the labels follow one or two rungs behind.
 */
export function tickSteps(pxPerHour: number): TickSteps {
  const px = (ms: number) => (ms / 3_600_000) * pxPerHour;
  const last = STEPS_MS[STEPS_MS.length - 1] as number;

  const stepMs = STEPS_MS.find((ms) => px(ms) >= MIN_TICK_PX) ?? last;
  const labelStepMs =
    STEPS_MS.find((ms) => px(ms) >= MIN_LABEL_PX && ms % stepMs === 0) ?? Math.max(stepMs, last);

  return { stepMs, labelStepMs };
}

export interface TimelineTick {
  /** epoch ms of the gridline */
  at: number;
  /** position as a fraction 0..1 of the window */
  offset: number;
  /** text for the axis, or null for an unlabelled gridline */
  label: string | null;
  /** local midnight: the panel draws a stronger line and names the day */
  midnight: boolean;
}

/**
 * Gridlines across the window, as fine as this zoom deserves.
 *
 * Positions are walked on the local wall clock from midnight rather than added
 * up in milliseconds, so the marks read 00:00, 06:00, 12:00 the way a clock
 * does — and stay on the clock across a daylight-saving change, which a fixed
 * millisecond stride would slide an hour off.
 */
export function timelineTicks(windowStart: number, windowEnd: number, pxPerHour: number): TimelineTick[] {
  const span = Math.max(1, windowEnd - windowStart);
  const { stepMs, labelStepMs } = tickSteps(pxPerHour);
  const stepMinutes = Math.round(stepMs / MINUTE);
  const labelStepMinutes = Math.round(labelStepMs / MINUTE);
  const ticks: TimelineTick[] = [];

  const cursor = new Date(windowStart);
  cursor.setHours(0, 0, 0, 0);
  while (cursor.getTime() < windowStart) cursor.setMinutes(cursor.getMinutes() + stepMinutes);

  for (; cursor.getTime() <= windowEnd; cursor.setMinutes(cursor.getMinutes() + stepMinutes)) {
    const at = cursor.getTime();
    const minuteOfDay = cursor.getHours() * 60 + cursor.getMinutes();
    const midnight = minuteOfDay === 0;
    ticks.push({
      at,
      offset: (at - windowStart) / span,
      label: midnight
        ? cursor.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })
        : minuteOfDay % labelStepMinutes === 0
          ? `${String(cursor.getHours()).padStart(2, '0')}:${String(cursor.getMinutes()).padStart(2, '0')}`
          : null,
      midnight,
    });
  }

  return ticks;
}

/** How the zoom control names the current stop: the interval the axis labels. */
export function zoomLabel(pxPerHour: number): string {
  const minutes = Math.round(tickSteps(pxPerHour).labelStepMs / MINUTE);
  return minutes < 60 ? `${minutes}m` : `${minutes / 60}h`;
}

/**
 * How the report groups a job's guests, worst first.
 *
 * The order is the whole point: a bar is clicked because it is not green, and
 * the guest that made it not green has to be the first thing on screen. The
 * tone names a colour the stylesheet already owns, so a group reads the same
 * as the bar it came from.
 */
const GUEST_GROUPS: ReadonlyArray<{ outcome: BackupGuestOutcome; title: string; tone: string }> = [
  { outcome: 'failed', title: 'Failed', tone: 'danger' },
  { outcome: 'warnings', title: 'Finished with warnings', tone: 'edge' },
  { outcome: 'running', title: 'Still running', tone: 'accent' },
  { outcome: 'unknown', title: 'Never finished', tone: 'danger' },
  { outcome: 'ok', title: 'Completed', tone: 'ok' },
];

export interface GuestGroup {
  outcome: BackupGuestOutcome;
  /** heading for the group */
  title: string;
  /** a pill/row colour class: danger, edge, accent, ok */
  tone: string;
  /** how the tally at the top of the report names this group */
  tally: string;
  rows: BackupGuestReport[];
}

/**
 * Split a report's guests into the groups the dialog draws.
 *
 * Empty groups are dropped rather than rendered as a heading with nothing
 * under it — "0 failed" is worth saying on a cluster summary, but not on a
 * report of one job that plainly succeeded.
 */
export function groupGuests(guests: readonly BackupGuestReport[]): GuestGroup[] {
  const groups: GuestGroup[] = [];

  for (const group of GUEST_GROUPS) {
    const rows = guests.filter((guest) => guest.outcome === group.outcome);
    if (rows.length === 0) continue;
    groups.push({
      ...group,
      // "4 went through" rather than "4 completed": the tally is read as a
      // sentence about the job, not as a repeat of the heading below it.
      tally: `${rows.length} ${group.outcome === 'ok' ? 'went through' : group.title.toLowerCase()}`,
      rows,
    });
  }

  return groups;
}
